/** @jsxImportSource @emotion/react */
import DateTimePicker from 'components/atoms/DateTimePicker'
import Select from 'components/atoms/Select'
import { billingClaimStatusSelectOptions } from 'components/lab/billingCharge/consts'
import { chargeOptionFilter, getChargeDropdownLabel, getShortChargeIdValue } from 'components/lab/billingCharge/utils'
import { explainAutoPost, getClaimReviewState } from 'components/lab/billingEraReports/claimState'
import {
  emptyPaymentMethodDetails,
  PAYMENT_METHODS,
  PaymentMethod,
  paymentMethodDetailsBody,
  PaymentMethodDetailsFields,
} from 'components/lab/billingPayment/PaymentMethodDetailsFields'
import {
  ChargeChainPaymentBySync,
  ChargePayerOption,
  useChargePayerChain,
} from 'components/lab/billingPayment/useChargePayerChain'
import { filterPayerOption, PayerOptionLabel, viaName } from 'components/lab/payer/PayerOptionLabel'
import { OrderInsuranceNotes } from 'components/lab/billingPayment/OrderInsuranceNotes'
import { openingWriteOff, writeOffFromAllowed } from 'components/lab/billingPayment/manualPostingPrefill'
import routes from 'components/lab/constants/routes'
import { NeedsWorkFlagModal } from 'components/lab/billingNeedsWork/NeedsWorkFlagModal'
import { ChargeNotes } from 'components/lab/billingNotes/ChargeNotes'
import { toNum } from 'components/lab/orderEdit/helpers'
import { TimeWrapper } from 'components/lab/orderEdit/PsgHstWorkflow/FollowUpForm'
import { PatientSection } from 'components/lab/patient/PatientDetails'
import { Form, Formik } from 'formik'
import React from 'react'
import { confirm } from 'components/molecules/Confirmation'
import { Alert, Button, Modal } from 'react-bootstrap'
import ReactSelect from 'react-select'
import { toast } from 'react-toastify'
import { useLazyGetBillingChargesQuery } from 'store/services/billingCharges'
import { useRecordEraDenialMutation } from 'store/services/billingDenials'
import {
  useCreateBillingPaymentMutation,
  useGetEraClaimPlanBalancesQuery,
  useGetEraPostingPlanQuery,
} from 'store/services/billingPayments'
import { useResolveNeedsWorkRemitMutation } from 'store/services/needsWork'
import { useGetLabPatientListQuery } from 'store/services/patient'
import { useGetLabPayersQuery } from 'store/services/payer'
import { BillingTransactionClaim, CreateBillingPaymentDto, EraPostingPlan } from 'store/types'
import { useDebounce } from 'use-debounce'
import * as Yup from 'yup'

type Props = {
  open: boolean
  onClose: () => void
  defaultPayerId?: number
  defaultPaymentDate?: string
  // The ERA pages pass the whole claim, which the denial actions read. Posting
  // a parked remit passes only its id and the payer's claim number; the rest
  // comes from the posting plan.
  claim: Pick<BillingTransactionClaim, 'id' | 'payerClaimControlNumber'> & Partial<BillingTransactionClaim>
  eraId: string
  claimId: string
  // Posting a remit parked under this Needs Work item, with corrections: the
  // form posts through resolving the item, which is what lets a parked remit
  // post at all.
  resolveNeedsWork?: { itemId: string; note?: string | null }
}

const validationSchema = Yup.object().shape({
  patientId: Yup.number().required('Required'),
  paymentByPayerId: Yup.number().required('Required'),
  paymentDate: Yup.string().required('Required'),
  paymentMethod: Yup.mixed<PaymentMethod>()
    .oneOf(PAYMENT_METHODS)
    .required('Required'),
  checkNumber: Yup.string().when('paymentMethod', {
    is: 'Check',
    then: (schema) => schema.required('Required'),
    otherwise: (schema) => schema.optional(),
  }),
  checkDate: Yup.string().when('paymentMethod', {
    is: 'Check',
    then: (schema) => schema.required('Required'),
    otherwise: (schema) => schema.optional(),
  }),
  // The remittance always carries the EFT trace and settlement date, so both
  // are required; the originating company id only reaches files uploaded
  // after it started being kept.
  payerClaimControlNumber: Yup.string().optional(),
  eftTraceNumber: Yup.string().when('paymentMethod', {
    is: 'ACH',
    then: (schema) => schema.required('Required'),
    otherwise: (schema) => schema.optional(),
  }),
  effectiveDate: Yup.string().when('paymentMethod', {
    is: 'ACH',
    then: (schema) => schema.required('Required'),
    otherwise: (schema) => schema.optional(),
  }),
  originatingCompanyId: Yup.string().optional(),
  // Card details are typed in by staff: asked for, not demanded — a NON
  // remittance (a denial) posts as a card payment with nothing to type.
  cardTransactionId: Yup.string().optional(),
  cardProcessedDate: Yup.string().optional(),
  cardLastFour: Yup.string()
    .matches(/^\d{4}$/, { message: 'Enter the last four digits', excludeEmptyString: true })
    .optional(),
  nextResponsiblePayerId: Yup.number().nullable().optional(),
  nextResponsiblePatientId: Yup.number().nullable().optional(),
  claimStatus: Yup.string().optional(),
  chargeId: Yup.string().required('Required'),
})

const patientPaymentsUrl = (patientId: number | string) =>
  `${routes.index}${routes.patient.root}/${patientId}?section=${PatientSection.Payments}`

const Money = ({ v }: { v?: string | number }) => (
  <span>{v ? `$${Number(v).toFixed(2)}` : '$0.00'}</span>
)

const Balance = ({ v }: { v?: number | null }) => (v === null || v === undefined ? <span>—</span> : <Money v={v} />)

const ClaimServiceLines: React.FC<{
  serviceLines?: any[]
  lineBalances?: (number | null)[]
  onChange: (index: number, field: string, value: number | string) => void
}> = ({ serviceLines, lineBalances, onChange }) => {
  const formatInitial = (value: unknown): string => {
    if (value === null || value === undefined || value === '') return ''
    if (typeof value === 'number') return value.toFixed(2)
    return String(value)
  }

  const handleTextChange = (idx: number, field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(idx, field, e.target.value)
  }

  const lines = serviceLines ?? []

  const knownBalances = (lineBalances ?? []).filter((balance): balance is number => balance !== null)
  const tBalance = knownBalances.length ? knownBalances.reduce((total, balance) => total + balance, 0) : null
  let tAllowed = 0
  let tPaid = 0
  let tCopay = 0
  let tWriteOff = 0

  const rows = lines.map((sl, idx) => {
    tAllowed += toNum(sl.allowed)
    tPaid += toNum(sl.paidRefund)
    tCopay += toNum(sl.copay)
    tWriteOff += toNum(sl.writeOff)

    return (
      <tr key={idx}>
        <td>{idx + 1}</td>
        <td>{sl.cptCode}</td>
        <td>
          <Balance v={lineBalances?.[idx]} />
        </td>
        <td>
          <input
            type="text"
            className="form-control form-control-sm rounded-lg text-md"
            value={formatInitial(sl.allowed)}
            onChange={handleTextChange(idx, 'allowed')}
          />
        </td>
        <td>
          <input
            type="text"
            className="form-control form-control-sm rounded-lg text-md"
            value={formatInitial(sl.paidRefund)}
            onChange={handleTextChange(idx, 'paidRefund')}
          />
        </td>
        <td>
          <input
            type="text"
            className="form-control form-control-sm rounded-lg text-md"
            value={formatInitial(sl.copay)}
            onChange={handleTextChange(idx, 'copay')}
          />
        </td>
        <td>
          <input
            type="text"
            className="form-control form-control-sm rounded-lg text-md"
            value={formatInitial(sl.writeOff)}
            onChange={handleTextChange(idx, 'writeOff')}
          />
        </td>
      </tr>
    )
  })

  return (
    <>
      {rows}
      <tr style={{ fontWeight: 600 }}>
        <td colSpan={2}>Total</td>
        <td>
          <Balance v={tBalance} />
        </td>
        <td>
          <Money v={tAllowed} />
        </td>
        <td>
          <Money v={tPaid} />
        </td>
        <td>
          <Money v={tCopay} />
        </td>
        <td>
          <Money v={tWriteOff} />
        </td>
      </tr>
    </>
  )
}

// Once the plan balances are in, a line the remittance left with no write-off
// gets balance minus allowed. Stated write-offs and denials (allowed $0) stay.
const EraWriteOffSync: React.FC<{
  serviceLines?: any[]
  lineBalances?: (number | null)[]
  setFieldValue: (field: string, value: unknown) => void
}> = ({ serviceLines, lineBalances, setFieldValue }) => {
  React.useEffect(() => {
    lineBalances?.forEach((balance, idx) => {
      const writeOff = serviceLines?.[idx] ? openingWriteOff(balance, serviceLines[idx]) : null

      if (writeOff !== null) {
        setFieldValue(`serviceLines[${idx}].writeOff`, writeOff.toFixed(2))
      }
    })
    // Applied once per balances fetch; later edits are the biller's.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineBalances])

  return null
}

const ServiceLines = ({
  serviceLines,
  lineBalances,
  onChange,
}: {
  serviceLines?: any[]
  lineBalances?: (number | null)[]
  onChange: (index: number, field: string, value: number | string) => void
}) => {
  return (
    <table className="table table-sm mb-0">
      <thead>
        <tr css={{ '& > th': { fontWeight: 500 } }}>
          <th>Sr. No</th>
          <th>CPT</th>
          <th>Balance</th>
          <th>Allowed</th>
          <th>Paid/Refund</th>
          <th>Transfers</th>
          <th>Write-Off</th>
        </tr>
      </thead>
      <tbody>
        <ClaimServiceLines serviceLines={serviceLines} lineBalances={lineBalances} onChange={onChange} />
      </tbody>
    </table>
  )
}


const PAYER_RANK_LABELS: { [position: number]: string } = { 1: 'primary', 2: 'secondary', 3: 'tertiary' }

const rankLabel = (position: number): string => PAYER_RANK_LABELS[position] ?? `payer ${position}`

// The charge claim status a posting by a COB rank implies (keys of
// billingClaimStatusOptions): a secondary posting leaves the charge billed to
// the secondary, not "Billed To Prim plan". Mirrors the server's suggestion.
const CLAIM_STATUS_BY_POSITION: { [position: number]: string } = {
  2: '4', // Billed To Sec plan (EDI)
  3: '6', // Billed to Tertiary plan - EDI
}

type RemittancePayer = NonNullable<EraPostingPlan['remittancePayer']>

// The chosen payer disagrees with the remittance — with the payer it names
// (Loop 1000A) when that payer sits on the charge, else with the rank CLP02
// states.
type PostingPayerMismatch = {
  // The remittance's payer as the 835 names it, when it is on the chain.
  remitPayerName: string | null
  // The rank the remittance says it processed the claim at (CLP02).
  stated?: number
  // The chosen payer and the rank it holds on the charge.
  selected: number
  payerName: string
  // Whether the charge carries a payer at the stated rank at all.
  chainHasStated: boolean
}

const describePostingPayerMismatch = (mismatch: PostingPayerMismatch): string => {
  const { remitPayerName, stated, selected, payerName, chainHasStated } = mismatch

  if (remitPayerName) {
    return `This remittance is from ${remitPayerName}, but ${payerName} is selected. `
      + `Select ${remitPayerName}, or posting will record the money as ${payerName}'s ${rankLabel(selected)} payment.`
  }

  if (!stated) {
    return ''
  }

  return `This remittance says the claim was processed by the ${rankLabel(stated)} payer, but ${payerName} is the ${rankLabel(selected)} payer on this charge. `
    + (chainHasStated
      ? `Select the ${rankLabel(stated)} payer, or posting will record a second ${rankLabel(selected)} payment.`
      : `This charge has no ${rankLabel(stated)} payer — add it to the charge before posting, or posting will record a second ${rankLabel(selected)} payment.`)
}

const postingPayerMismatchTitle = ({ remitPayerName, selected, payerName }: PostingPayerMismatch): string =>
  remitPayerName
    ? `This remittance is from ${remitPayerName}. Post it as ${payerName} anyway?`
    : `Post as the ${rankLabel(selected)} payer anyway?`

// The remittance names a payer the charge does not carry, so nothing is
// pre-selected, and whatever the biller picks is not the payer that paid. Said
// in as many words, because the usual cause is the claim being matched to the
// wrong charge, not a missing payer.
const describeUnplacedRemittance = (remit: RemittancePayer, selectedName: string | null): string =>
  `This remittance is from ${remit.name}, which is not on this charge's payer list`
  + (selectedName
    ? `, but ${selectedName} is selected. `
    : ', so nothing is pre-selected. ')
  + 'That usually means the claim is matched to the wrong charge.'

// The remittance's payer is nowhere on the charge, with the payer the biller
// chose in its place, if any.
type UnplacedRemittance = {
  remit: RemittancePayer
  selectedName: string | null
}

// Our payer records could not say who sent the remittance, so the payer the
// form pre-selected rests on its rank on the claim alone.
const describeUnrecognizedRemittance = (remit: RemittancePayer, selectedName: string): string =>
  `This remittance is from ${remit.name}, which could not be matched to a payer on this charge by name or ID, `
  + `so ${selectedName} is pre-selected by its rank on the claim. Check it before posting.`

// What the form says under Payment By about the payer chosen against the
// remittance: that the remittance's payer is not on the charge at all, ahead
// of anything else; a warning when the chosen payer disagrees with it; and
// when neither, that the remittance's payer could not be recognised.
const PostingPayerNotice = ({
  mismatch,
  unplaced,
  unrecognized,
}: {
  mismatch: PostingPayerMismatch | null
  unplaced: UnplacedRemittance | null
  unrecognized: { remit: RemittancePayer; selectedName: string } | null
}) => {
  if (unplaced) {
    return (
      <Alert variant={unplaced.selectedName ? 'warning' : 'info'} className="mt-2 mb-0 p-2" css={{ fontSize: 12 }}>
        {describeUnplacedRemittance(unplaced.remit, unplaced.selectedName)}
      </Alert>
    )
  }

  if (mismatch) {
    return (
      <Alert variant="warning" className="mt-2 mb-0 p-2" css={{ fontSize: 12 }}>
        {describePostingPayerMismatch(mismatch)}
      </Alert>
    )
  }

  if (unrecognized) {
    return (
      <Alert variant="info" className="mt-2 mb-0 p-2" css={{ fontSize: 12 }}>
        {describeUnrecognizedRemittance(unrecognized.remit, unrecognized.selectedName)}
      </Alert>
    )
  }

  return null
}

export const ApplyPaymentModal: React.FC<Props> = ({
  open,
  onClose,
  defaultPayerId,
  defaultPaymentDate,
  claim,
  eraId,
  claimId,
  resolveNeedsWork,
}) => {
  const [patientSearch, setPatientSearch] = React.useState<string>('')
  // The charge the remit would be parked on, while the Needs Work form is open.
  const [parkingChargeId, setParkingChargeId] = React.useState<string | null>(null)
  const [payerSearch, setPayerSearch] = React.useState<string>('')
  const [selectedPatientId, setSelectedPatientId] = React.useState<number | undefined>(undefined)
  const openPaymentsRef = React.useRef(false)

  const [debouncedPayerSearch] = useDebounce(payerSearch, 300)

  const {
    data: plan,
    isFetching: prefillLoading,
  } = useGetEraPostingPlanQuery(
    { eraId, claimId: claim?.id as string },
    { skip: !open || !claim?.id, refetchOnMountOrArgChange: 30 },
  )

  const payerPatientId = React.useMemo(
    () => selectedPatientId || plan?.patientId || undefined,
    [selectedPatientId, plan?.patientId],
  )

  const payerQueryParams = React.useMemo(
    () => ({
      name: debouncedPayerSearch,
      active: true,
      ...(payerPatientId ? { patientId: payerPatientId } : {}),
    }),
    [debouncedPayerSearch, payerPatientId],
  )

  const { data: payers } = useGetLabPayersQuery(payerQueryParams)

  const [createPayment, { isLoading: isCreating }] = useCreateBillingPaymentMutation()
  const [resolveNeedsWorkRemit, { isLoading: isResolving }] = useResolveNeedsWorkRemitMutation()
  const isLoading = isCreating || isResolving
  const [recordEraDenial, { isLoading: isRecordingDenial }] = useRecordEraDenialMutation()

  // A denied remittance is recorded as a denial by default; posting it
  // instead moves money by its codes and closes the denial. Only a remittance
  // that paid nothing can be recorded — money received is always posted.
  // A parked remit is decided through its Needs Work item, so the form that
  // posts it has no denial path.
  const eraClaim = resolveNeedsWork ? undefined : claim as BillingTransactionClaim
  const reviewState = eraClaim ? getClaimReviewState(eraClaim) : undefined
  const isDenied = reviewState === 'DENIED'
  // Why auto-posting left this remittance for a person (or when it will post).
  const autoPost = eraClaim ? explainAutoPost(eraClaim) : null
  const canRecordDenial = !!eraClaim && toNum(eraClaim.paidAmount) === 0 && reviewState !== 'REVERSAL'
    && !eraClaim.denialId

  const recordDenial = async (values: {
    patientId?: number
    chargeId?: string
    paymentByPayerId?: number
    paymentDate: string
  }) => {
    if (!values.chargeId || !values.paymentByPayerId) {
      toast.error('Select the charge and the denying payer first')
      return
    }

    try {
      await confirm('', {
        title: 'Record as denial?',
        description:
          'The remittance leaves the ERA bucket and its codes and amounts are kept on the charge. '
          + 'Nothing posts: the balance stays open until the denial is written off, pushed to the patient or resubmitted.',
        yesAction: 'Record denial',
        noAction: 'Cancel',
      })
    } catch {
      return
    }

    try {
      await recordEraDenial({
        eraId,
        claimId: claim.id,
        chargeId: values.chargeId,
        paymentByPayerId: Number(values.paymentByPayerId),
        denialDate: values.paymentDate,
      }).unwrap()

      toast.success('Denial recorded on the charge')

      if (openPaymentsRef.current && values.patientId) {
        window.open(patientPaymentsUrl(Number(values.patientId)), '_blank', 'noopener,noreferrer')
      }

      onClose()
    } catch (e: any) {
      toast.error(e?.data?.message || 'Failed to record the denial')
    } finally {
      openPaymentsRef.current = false
    }
  }

  const [chargeSearch, setChargeSearch] = React.useState<string>('')

  const chargePatientId = React.useMemo(
    () => selectedPatientId || plan?.patientId || undefined,
    [selectedPatientId, plan?.patientId],
  )

  const [fetchCharges, { data: charges }] = useLazyGetBillingChargesQuery()

  // Which charge the chain is read from: the ERA prefill picks it for a matched
  // claim, the Charge select overrides it.
  const {
    selectedChargeId,
    setSelectedChargeId,
    isChainLoaded,
    getChargeChain,
    getChargePayerOptions,
    getDerivedNextResponsible,
    getNextResponsibleLabel,
  } = useChargePayerChain({ patientId: chargePatientId, listedCharges: charges?.items })

  const payerOptions = React.useMemo(
    () =>
      (payers ?? []).map((p): ChargePayerOption => ({
        value: p.payerId,
        label: p.name,
        payerName: p.name,
        matchedName: p.matchedName,
      })),
    [payers],
  )

  // Payment By, with the remittance's own name beside the payer it was matched to.
  const paymentByOptions = React.useCallback(
    (chargeId?: string): ChargePayerOption[] => {
      const remit = plan?.remittancePayer

      return (getChargePayerOptions(chargeId) ?? payerOptions).map((option) =>
        remit?.payerId && Number(option.value) === Number(remit.payerId)
          ? { ...option, matchedName: viaName(option.payerName, remit.name) }
          : option,
      )
    },
    [plan, getChargePayerOptions, payerOptions],
  )

  // The chosen payer against what the remittance says: the payer it names
  // (Loop 1000A) when that payer is on the charge, else the rank CLP02 states.
  // A crossover secondary's remittance echoes the primary's claim number, so a
  // claim-number match alone reads it as a second primary payment: PR
  // transferred onward instead of billed, plan balance left open, charge stuck
  // in Crossed Over. The automatic path refuses such a posting; here the
  // biller is warned, and asked again before posting.
  const postingPayerMismatch = React.useCallback(
    (chargeId?: string, paymentByPayerId?: number): PostingPayerMismatch | null => {
      if (!chargeId || String(chargeId) !== String(plan?.chargeId ?? '')) {
        return null
      }

      const chain = getChargeChain(chargeId)
      const selected = chain.find((payer) => Number(payer.payerId) === Number(paymentByPayerId))

      if (!selected) {
        return null
      }

      const selectedPosition = Number(selected.position)
      const payerName = selected.payerName || 'The selected payer'
      const remit = plan?.remittancePayer
      const stated = plan?.statedPayerPosition

      if (remit?.position) {
        if (Number(remit.payerId) === Number(selected.payerId)) {
          return null
        }

        return { remitPayerName: remit.name, stated, selected: selectedPosition, payerName, chainHasStated: true }
      }

      if (!stated || selectedPosition === stated) {
        return null
      }

      return {
        remitPayerName: null,
        stated,
        selected: selectedPosition,
        payerName,
        chainHasStated: chain.some((payer) => Number(payer.position) === stated),
      }
    },
    [plan, getChargeChain],
  )

  // The remittance names a payer our records know, and it is none of the
  // charge's payers. The server still falls back to the rank CLP02 states so a
  // headless posting has someone to post to, but the form does not pre-select
  // that payer: it opens with Payment By empty, says why, and keeps saying so
  // for whatever payer the biller picks instead.
  const remittanceUnplaced = !!plan?.chargeId
    && !!plan.remittancePayer?.name
    && plan.remittancePayer.placement === 'NOT_ON_CHARGE'

  const unplacedRemittance = React.useCallback(
    (chargeId?: string, paymentByPayerId?: number): UnplacedRemittance | null => {
      if (!remittanceUnplaced || !plan?.remittancePayer || String(chargeId ?? '') !== String(plan.chargeId)) {
        return null
      }

      const selected = paymentByPayerId
        ? getChargeChain(chargeId).find((payer) => Number(payer.payerId) === Number(paymentByPayerId))
        : undefined

      return {
        remit: plan.remittancePayer,
        selectedName: paymentByPayerId ? selected?.payerName || 'another payer' : null,
      }
    },
    [plan, remittanceUnplaced, getChargeChain],
  )

  // Nothing we hold recognises the remittance's payer — routine for
  // Medicare, whose remittance names rarely match our records — so the payer
  // pre-selected by rank stands, with a word that nothing else confirmed it.
  // Only while the biller keeps that payer: another choice is judged against
  // the rank the remittance states instead.
  const unrecognizedRemittance = React.useCallback(
    (chargeId?: string, paymentByPayerId?: number): { remit: RemittancePayer; selectedName: string } | null => {
      const remit = plan?.remittancePayer

      if (
        !remit?.name
        || remit.placement !== 'UNKNOWN'
        || !paymentByPayerId
        || Number(paymentByPayerId) !== Number(plan?.paymentByPayerId)
        || String(chargeId ?? '') !== String(plan?.chargeId ?? '')
      ) {
        return null
      }

      const selected = getChargeChain(chargeId).find((payer) => Number(payer.payerId) === Number(paymentByPayerId))

      return { remit, selectedName: selected?.payerName || plan?.paymentByPayerName || 'the selected payer' }
    },
    [plan, getChargeChain],
  )

  // The charge claim status that fits posting the chosen payer: what the
  // server suggested for its own choice, the COB rank's status for a payer the
  // biller switched to, and the charge's current status for a primary.
  const claimStatusFor = React.useCallback(
    (chargeId?: string, paymentByPayerId?: number): string => {
      if (paymentByPayerId && Number(paymentByPayerId) === Number(plan?.paymentByPayerId)) {
        return plan?.suggestedClaimStatus ?? plan?.claimStatus ?? ''
      }

      const position = getChargeChain(chargeId).find(
        (payer) => Number(payer.payerId) === Number(paymentByPayerId),
      )?.position

      return (position && CLAIM_STATUS_BY_POSITION[Number(position)]) || plan?.claimStatus || ''
    },
    [plan, getChargeChain],
  )

  // The carrier a forwarded claim went to, when the plan resolved it and the
  // form still describes the plan's charge and payer: posting adds it behind
  // the paying payer, so it is the next responsible even though the chain the
  // form holds does not carry it yet. The server derives the same answer.
  const crossoverPayerToAdd = React.useCallback(
    (chargeId?: string, paymentByPayerId?: number): string | null => {
      const crossover = plan?.crossover

      if (
        crossover?.status !== 'RESOLVED'
        || String(chargeId ?? '') !== String(plan?.chargeId ?? '')
        || Number(paymentByPayerId) !== Number(plan?.paymentByPayerId)
      ) {
        return null
      }

      return crossover.payer.name || 'Next payer'
    },
    [plan],
  )

  const initialFormikValues = React.useMemo(
    () => ({
      patientId: plan?.patientId,
      paymentByPayerId: remittanceUnplaced ? undefined : plan?.paymentByPayerId ?? defaultPayerId,
      chargeId: plan?.chargeId,
      claimStatus: (remittanceUnplaced ? undefined : plan?.suggestedClaimStatus) ?? plan?.claimStatus ?? '',
      paymentMethod: (plan?.paymentMethod ?? 'Card') as PaymentMethod,
      // Whatever the remittance already says is filled in; the biller corrects
      // rather than re-types.
      ...emptyPaymentMethodDetails(),
      payerClaimControlNumber: plan?.payerClaimControlNumber ?? claim?.payerClaimControlNumber ?? '',
      checkNumber: plan?.checkNumber ?? '',
      checkDate: plan?.checkDate ?? '',
      eftTraceNumber: plan?.eftTraceNumber ?? '',
      effectiveDate: plan?.effectiveDate ?? '',
      originatingCompanyId: plan?.originatingCompanyId ?? '',
      paymentDate: defaultPaymentDate || plan?.paymentDate || new Date().toISOString(),
      serviceLines: plan?.serviceLines ?? [],
    }),
    [plan, remittanceUnplaced, claim?.payerClaimControlNumber, defaultPayerId, defaultPaymentDate],
  )

  const [debouncedPatientSearch] = useDebounce(patientSearch, 300)
  const { data: patients } = useGetLabPatientListQuery({
    size: 100,
    page: 0,
    name: debouncedPatientSearch,
    id: !debouncedPatientSearch ? (selectedPatientId || plan?.patientId) : undefined,
  })

  const patientOptions = React.useMemo(
    () =>
      (patients?.items ?? []).map((p) => ({
        value: String(p.id),
        label: `${p.lastName}, ${p.firstName} (${p.dob})`,
      })),
    [patients?.items],
  )

  React.useEffect(() => {
    if (plan?.patientId && !selectedPatientId) {
      setSelectedPatientId(plan.patientId)
    }
  }, [plan?.patientId, selectedPatientId])

  // What the plans still owe on the chosen charge; unknown while it loads.
  const { currentData: planBalances } = useGetEraClaimPlanBalancesQuery(
    { eraId, claimId: claim?.id as string, chargeId: selectedChargeId as string },
    { skip: !open || !claim?.id || !selectedChargeId, refetchOnMountOrArgChange: true },
  )
  const lineBalances = selectedChargeId ? planBalances?.lineBalances : undefined

  React.useEffect(() => {
    if (plan?.chargeId && !selectedChargeId) {
      setSelectedChargeId(String(plan.chargeId))
    }
  }, [plan?.chargeId, selectedChargeId])

  React.useEffect(() => {
    if (!open) {
      setSelectedPatientId(undefined)
      setSelectedChargeId(undefined)
      setPatientSearch('')
      setPayerSearch('')
      setChargeSearch('')
    }
  }, [open])

  React.useEffect(() => {
    if (!open || !chargePatientId) {
      return
    }

    // Client-side search (chargeOptionFilter): the server-side orderId filter
    // is an exact match and cannot search by prefix or by charge ID.
    fetchCharges({
      page: 0,
      size: 100,
      patientId: Number(chargePatientId),
    })
  }, [open, chargePatientId, fetchCharges])


  const mapCharge = (c: any) => ({
    value: String(c.id),
    label: getChargeDropdownLabel(c),
    chargeId: String(c.id),
    orderId: c.orderId,
  })

  const chargeOptions = React.useMemo(() => {
    if (!chargePatientId) {
      return []
    }

    return (charges?.items ?? []).map(mapCharge)
  }, [chargePatientId, charges])

  return (
    <Modal
      show={open}
      onHide={onClose}
      css={{
        '.modal-content': {
          borderRadius: 15,
        },
        '.modal-body': {
          padding: '20px 30px',
        },
      }}
      size="xl"
      centered>
      <div
        css={{
          textAlign: 'center',
          padding: '20px 0',
          fontWeight: 600,
          fontSize: 22,
        }}>
        {resolveNeedsWork ? 'Post Parked Remit with Corrections' : isDenied ? 'Record Denial' : 'Apply Payment'}
      </div>
      <Formik
        key={`${claimId}-${open ? 'open' : 'closed'}`}
        enableReinitialize
        validateOnMount
        validationSchema={validationSchema}
        initialValues={initialFormikValues}
        onSubmit={async (values, { setSubmitting }) => {
          try {
            if (!values?.serviceLines) {
              toast.error('Claim not found. Please reach out to support')
              return
            }

            const payerName =
              getChargePayerOptions(values.chargeId)?.find(
                (p) => Number(p.value) === Number(values.paymentByPayerId),
              )?.payerName ||
              payerOptions.find((p) => Number(p.value) === Number(values.paymentByPayerId))?.label ||
              'Unknown'

            // The remittance names a different rank than the chosen payer holds:
            // a second look before the money lands on the wrong payer.
            const mismatch = postingPayerMismatch(
              values.chargeId,
              values.paymentByPayerId ? Number(values.paymentByPayerId) : undefined,
            )

            // The remittance's payer is not on the charge at all: asked about
            // that rather than about the rank, which is beside the point.
            const unplaced = unplacedRemittance(
              values.chargeId,
              values.paymentByPayerId ? Number(values.paymentByPayerId) : undefined,
            )

            if (unplaced?.selectedName) {
              try {
                await confirm('', {
                  title: `This remittance is from ${unplaced.remit.name}. Post it as ${unplaced.selectedName} anyway?`,
                  description: describeUnplacedRemittance(unplaced.remit, unplaced.selectedName),
                  yesAction: 'Post anyway',
                  noAction: 'Cancel',
                })
              } catch {
                setSubmitting(false)
                return
              }
            } else if (mismatch) {
              try {
                await confirm('', {
                  title: postingPayerMismatchTitle(mismatch),
                  description: describePostingPayerMismatch(mismatch),
                  yesAction: 'Post anyway',
                  noAction: 'Cancel',
                })
              } catch {
                setSubmitting(false)
                return
              }
            }

            if (isDenied) {
              try {
                await confirm('', {
                  title: 'Post this denied remittance?',
                  description:
                    'Posting moves money by the codes on the remittance and resolves the denial. '
                    + 'To keep the denial open without posting, use Record as Denial.',
                  yesAction: 'Post anyway',
                  noAction: 'Cancel',
                })
              } catch {
                setSubmitting(false)
                return
              }
            }

            const derivedNextResponsible = getDerivedNextResponsible(
              values.chargeId,
              values.paymentByPayerId ? Number(values.paymentByPayerId) : undefined,
            )

            const serviceLinesWithTransactions = values.serviceLines.map((sl) => {
              const allowed = Number(toNum(sl.allowed).toFixed(2))
              const paidRefund = Number(toNum(sl.paidRefund).toFixed(2))
              const transfers = Number(toNum(sl.transfers).toFixed(2))
              const copay = Number(toNum(sl.copay).toFixed(2))
              const writeOff = Number(toNum(sl.writeOff).toFixed(2))

              return {
                ...sl,
                balance: Number(toNum(sl.balance).toFixed(2)),
                allowed,
                paidRefund,
                transfers,
                copay,
                writeOff,
                transactions: [
                  {
                    date: values.paymentDate,
                    txId: claimId,
                    paymentBy: payerName,
                    allowed,
                    paidRefund,
                    transfers,
                    copay,
                    writeOff,
                  },
                ],
              }
            })

            const payment: CreateBillingPaymentDto = {
              patientId: Number(values.patientId),
              paymentByPayerId: Number(values.paymentByPayerId),
              nextResponsiblePayerId: derivedNextResponsible.type === 'PAYER'
                ? derivedNextResponsible.payerId
                : undefined,
              nextResponsiblePatientId: derivedNextResponsible.type === 'PATIENT'
                ? Number(values.patientId)
                : undefined,
              paymentMethod: values.paymentMethod,
              ...paymentMethodDetailsBody(values),
              paymentDate: values.paymentDate,
              appointmentDate: values.serviceLines?.[0]?.startDateOfService || values.paymentDate,
              status: 'APPLIED',
              serviceData: serviceLinesWithTransactions,
              eraId,
              claimId: claim.id,
              claimNumber: claimId,
              chargeId: values.chargeId,
              claimStatus: values.claimStatus || undefined,
            }

            if (resolveNeedsWork) {
              await resolveNeedsWorkRemit({
                id: resolveNeedsWork.itemId,
                claimId: claim.id,
                note: resolveNeedsWork.note || null,
                remitAction: 'POST_WITH_CORRECTIONS',
                payment,
              }).unwrap()

              toast.success('Remit posted — Needs Work item resolved')
            } else {
              await createPayment(payment).unwrap()

              toast.success('Payment applied successfully')
            }

            if (openPaymentsRef.current) {
              window.open(
                patientPaymentsUrl(Number(values.patientId)),
                '_blank',
                'noopener,noreferrer',
              )
            }

            onClose()
          } catch (e: any) {
            toast.error(e?.data?.message || 'Failed to apply payment')
          } finally {
            openPaymentsRef.current = false
            setSubmitting(false)
          }
        }}>
        {({ values, setFieldValue, submitForm, isSubmitting, isValid }) => (
          <Form>
            <ChargeChainPaymentBySync
              chargeId={values.chargeId}
              paymentByPayerId={values.paymentByPayerId}
              chainLoaded={isChainLoaded(values.chargeId)}
              chain={getChargeChain(values.chargeId)}
              setFieldValue={setFieldValue}
            />
            <EraWriteOffSync
              serviceLines={values.serviceLines}
              lineBalances={lineBalances}
              setFieldValue={setFieldValue}
            />
            <Modal.Body>
              {prefillLoading && (
                <div className="mb-2" css={{ fontSize: 12 }}>
                  Loading...
                </div>
              )}
              {isDenied && (
                <div className="alert alert-danger mb-3 py-2" css={{ fontSize: 12 }}>
                  The payer denied this claim. Record it as a denial to keep its codes and amounts on the
                  charge without posting money. Applying the payment instead posts it by its codes and
                  closes the denial.
                </div>
              )}
              {autoPost && (
                <div
                  className={`alert ${autoPost.state === 'EXCEPTION' ? 'alert-warning' : 'alert-info'} mb-3 py-2`}
                  css={{ fontSize: 12 }}
                  data-testid="auto-post-notice">
                  <div css={{ fontWeight: 600 }}>{autoPost.title}</div>
                  {autoPost.reasons.map((reason) => (
                    <div key={reason}>• {reason}</div>
                  ))}
                </div>
              )}
              {!prefillLoading && plan?.chargeSuggested && (
                <div
                  className="alert alert-warning mb-3 py-2"
                  css={{ fontSize: 12 }}
                  data-testid="suggested-charge-notice">
                  The payer did not echo a control number we hold for this claim. The charge,
                  patient and payer below were matched by member ID and date of service — please
                  verify them before applying. Posting confirms the match.
                </div>
              )}
              <div className="row">
                <div className="col" css={{ width: '100%' }}>
                  <label className="textGray">Payment By</label>
                  <ReactSelect
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 1051 }) }}
                    className="basic-single"
                    classNamePrefix="select"
                    isClearable
                    isSearchable
                    isDisabled={prefillLoading}
                    placeholder="Select Payor"
                    name="paymentByPayerId"
                    options={paymentByOptions(values.chargeId)}
                    formatOptionLabel={(option) => <PayerOptionLabel name={option.label} via={option.matchedName} />}
                    filterOption={filterPayerOption}
                    value={
                      paymentByOptions(values.chargeId).find(
                        (o) => Number(o.value) === Number(values.paymentByPayerId),
                      ) || null
                    }
                    onChange={(item) => {
                      const payerId = item ? Number(item.value) : undefined

                      setFieldValue('paymentByPayerId', payerId)
                      // The dependent default follows the payer: a secondary
                      // posting must not leave "Billed To Prim plan" behind.
                      setFieldValue('claimStatus', claimStatusFor(values.chargeId, payerId))
                    }}
                    onInputChange={(v) => setPayerSearch(v)}
                    inputValue={payerSearch}
                    onBlur={() => setPayerSearch('')}
                  />
                  <PostingPayerNotice
                    mismatch={postingPayerMismatch(
                      values.chargeId,
                      values.paymentByPayerId ? Number(values.paymentByPayerId) : undefined,
                    )}
                    unplaced={unplacedRemittance(
                      values.chargeId,
                      values.paymentByPayerId ? Number(values.paymentByPayerId) : undefined,
                    )}
                    unrecognized={unrecognizedRemittance(
                      values.chargeId,
                      values.paymentByPayerId ? Number(values.paymentByPayerId) : undefined,
                    )}
                  />
                </div>
                <div className="col" css={{ width: '100%' }}>
                  <label className="textGray">Next Responsible</label>
                  <input
                    type="text"
                    className="form-control"
                    disabled
                    title={
                      crossoverPayerToAdd(values.chargeId, values.paymentByPayerId)
                        ? 'The remittance forwarded the claim to this payer; posting adds it to the charge'
                        : "Derived from the selected charge's payer chain"
                    }
                    value={
                      crossoverPayerToAdd(values.chargeId, values.paymentByPayerId)
                        ? `${crossoverPayerToAdd(values.chargeId, values.paymentByPayerId)} (added from this remittance)`
                        : getNextResponsibleLabel(values.chargeId, values.paymentByPayerId)
                    }
                  />
                </div>
                <div className="col" css={{ width: '100%' }}>
                  <label className="textGray">Claim Status</label>
                  <Select
                    value={values.claimStatus}
                    name="claimStatus"
                    disabled={prefillLoading}
                    handleChange={(e) => setFieldValue('claimStatus', e.target.value)}
                    options={billingClaimStatusSelectOptions}
                  />
                </div>
                <div className="col" css={{ width: '100%' }}>
                  <label className="textGray">Payment Date</label>
                  <TimeWrapper>
                    <DateTimePicker
                      value={values.paymentDate ? new Date(values.paymentDate) : null}
                      handleChange={(d) => {
                        setFieldValue('paymentDate', d ? d.toISOString() : '')
                      }}
                      showTimeSelect={false}
                      isClearable
                      disabled={prefillLoading}
                      dateFormat="MMMM d, yyyy"
                    />
                  </TimeWrapper>
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-6 col-md-3">
                  <label className="textGray">Charge</label>
                  <ReactSelect
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 1051 }) }}
                    className="basic-single"
                    classNamePrefix="select"
                    isClearable
                    isSearchable
                    isDisabled={prefillLoading}
                    placeholder="Search by Order ID or Charge ID"
                    name="chargeId"
                    options={chargeOptions}
                    filterOption={chargeOptionFilter}
                    onInputChange={(v) => setChargeSearch(v)}
                    inputValue={chargeSearch}
                    value={
                      chargeOptions.find((option) => String(option.value) === String(values.chargeId)) ||
                      null
                    }
                    onChange={(item) => {
                      const nextChargeId = item ? String(item.value) : undefined
                      setFieldValue('chargeId', nextChargeId)
                      setSelectedChargeId(nextChargeId)
                      // Payment By is reconciled by ChargeChainPaymentBySync
                      // once the charge's real chain arrives — the listing row
                      // only knows two payers and would wipe a tertiary here.
                    }}
                  />
                </div>
                <div className="col-6 col-md-3">
                  <label className="textGray">Patient</label>
                  <ReactSelect
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 1051 }) }}
                    className="basic-single"
                    classNamePrefix="select"
                    isClearable
                    isSearchable
                    isDisabled={prefillLoading}
                    placeholder="Search Patient"
                    name="patientId"
                    options={patientOptions}
                    value={
                      patientOptions.find((o) => Number(o.value) === Number(values.patientId)) ||
                      null
                    }
                    onChange={(item) => {
                      const patientId = item ? Number(item.value) : undefined
                      setFieldValue('patientId', patientId)
                      setSelectedPatientId(patientId)
                      setPatientSearch('')
                      setChargeSearch('')
                      setFieldValue('chargeId', undefined)
                      setSelectedChargeId(undefined)
                    }}
                    onInputChange={(v) => setPatientSearch(v)}
                    inputValue={patientSearch}
                  />
                </div>
                <div className="col-6 col-md-3">
                  <label className="textGray">Payment Method</label>
                  <Select
                    value={values.paymentMethod}
                    name="paymentMethod"
                    disabled={prefillLoading}
                    handleChange={(e) => setFieldValue('paymentMethod', e.target.value)}
                    options={[
                      { key: 'Card', value: 'Card' },
                      { key: 'ACH', value: 'ACH' },
                      { key: 'Check', value: 'Check' },
                      { key: 'Other', value: 'Other' },
                    ]}
                  />
                </div>
                <PaymentMethodDetailsFields
                  values={values}
                  setFieldValue={setFieldValue}
                  disabled={prefillLoading}
                />
              </div>
              <hr className="py-1" />
              <ServiceLines
                serviceLines={values?.serviceLines}
                lineBalances={lineBalances}
                onChange={(i, field, val) => {
                  setFieldValue(`serviceLines[${i}].${field}`, val)

                  if (field === 'allowed') {
                    const base = lineBalances?.[i] ?? values.serviceLines?.[i]?.charged
                    setFieldValue(`serviceLines[${i}].writeOff`, writeOffFromAllowed(base, val).toFixed(2))
                  }
                }}
              />
              <div
                className="mt-3 p-3"
                css={{
                  background: '#F4F6F9',
                  borderRadius: 10,
                }}>
                <div
                  css={{
                    fontWeight: 600,
                    fontSize: 16,
                  }}>
                  Notes
                  {values.chargeId && (
                    <span css={{ fontWeight: 400, fontSize: 13, color: '#6c757d', marginLeft: 6 }}>
                      on charge {getShortChargeIdValue(String(values.chargeId))}
                    </span>
                  )}
                </div>
                <div className="mt-2" data-testid="apply-payment-charge-notes">
                  <ChargeNotes chargeId={values.chargeId ? String(values.chargeId) : undefined} />
                </div>
                <OrderInsuranceNotes patientId={values.patientId} chargeId={values.chargeId} />
              </div>
            </Modal.Body>
            <Modal.Footer className="d-flex justify-content-end">
              {!resolveNeedsWork && (
                <Button
                  variant="outline-danger"
                  className="mr-auto"
                  disabled={!values.chargeId || isSubmitting || isLoading || prefillLoading}
                  title={values.chargeId
                    ? 'Park the remit on this charge instead of posting it, for someone to follow up'
                    : 'Pick the charge first'}
                  onClick={() => setParkingChargeId(values.chargeId ? String(values.chargeId) : null)}>
                  Needs Work
                </Button>
              )}
              <Button variant="outline-secondary" onClick={onClose}>
                Cancel
              </Button>
              {!resolveNeedsWork && (
                <Button
                  type="button"
                  variant={isDenied ? 'danger' : 'outline-danger'}
                  title={canRecordDenial ? undefined : 'Only a remittance that paid nothing can be recorded as a denial'}
                  disabled={
                    !canRecordDenial || !values.chargeId || !values.paymentByPayerId
                    || isSubmitting || isLoading || isRecordingDenial || prefillLoading
                  }
                  onClick={() => recordDenial(values)}>
                  Record as Denial
                </Button>
              )}
              <Button
                type="button"
                variant="outline-primary"
                disabled={!isValid || isSubmitting || isLoading || isRecordingDenial || prefillLoading}
                onClick={() => {
                  openPaymentsRef.current = true
                  submitForm()
                }}>
                Apply &amp; Open Payments
              </Button>
              <Button
                type="submit"
                variant={isDenied ? 'outline-primary' : 'primary'}
                disabled={!isValid || isSubmitting || isLoading || isRecordingDenial || prefillLoading}>
                Apply Payment
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
      {parkingChargeId && (
        <NeedsWorkFlagModal
          target={{ kind: 'remit', eraId, claimId: claim.id, chargeId: parkingChargeId }}
          onClose={() => setParkingChargeId(null)}
          onDone={onClose}
        />
      )}
    </Modal>
  )
}
